"use client";

import { useState } from "react";
import { Link2 } from "lucide-react";
import {
  BookingLinkBuilderPanel,
  Button,
  ResponsiveModal,
  useUniversalAuthOptional,
} from "@wtc/ui";

/**
 * Admin-only affordance rendered on the PUBLIC booking page.
 *
 * The public booking route is unauthenticated and used by clients. This panel
 * must never appear for anonymous visitors or non-admin users. Gating relies on
 * useUniversalAuthOptional() (non-throwing): it degrades to a logged-out shape
 * when auth is unresolved, so the default is "hidden". We render nothing at all
 * — no trigger, no markup — unless the viewer is an authenticated admin.
 *
 * There is no sensitive data here: the builder only composes public URL params
 * from the public event-type catalog and the fixed team list. The gate is a UX
 * boundary (keep admin tooling out of the client flow), not a data boundary.
 */
export function PublicBookingAdminPanel() {
  const { isAuthenticated, isAdmin } = useUniversalAuthOptional();
  const [open, setOpen] = useState(false);

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-4 right-4 z-40">
        <Button
          type="button"
          onClick={() => setOpen(true)}
          className="shadow-lg"
          variant="secondary"
        >
          <Link2 className="mr-2 h-4 w-4" />
          Build booking link
        </Button>
      </div>

      <ResponsiveModal forceModal
        open={open}
        onOpenChange={setOpen}
        title={
          <span className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Build a booking link
          </span>
        }
        description="Generate a shareable link for this booking page with an event type or a specific person pre-selected. Send it to a client so they land on the right flow. Admins only."
        maxWidth="lg"
      >
        <BookingLinkBuilderPanel />
      </ResponsiveModal>
    </>
  );
}
